import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { AppButton, AppEmptyState, AppPillSwitch } from '../../components/ui';
import { formatMinutes } from '../../utils/tempsWeek';
import TempsDurationInput, { toEstimatedMinutes } from './TempsDurationInput';

const PERSON_COLORS = ['#2f6fb5', '#a4552c', '#2e7d6b', '#5f6a2a', '#b0463f', '#4d5fa0', '#8a6420', '#3f6f8f'];

const BRAND_META = {
  iti: { key: 'iti', label: 'itiConseil', tone: 'iti', flag: 'isIti' },
  kkp: { key: 'kkp', label: 'Koikispass', tone: 'kkp', flag: 'isKkp' },
  klub: { key: 'klub', label: 'Klub', tone: 'klub', flag: 'isKlub' },
};

const emptyForm = () => ({ title: '', amount: '30', unit: 'min', who: '', tried: false });

const initials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const colorForUser = (id) => {
  const n = Number(id) || 0;
  return PERSON_COLORS[Math.abs(n) % PERSON_COLORS.length];
};

const productBrands = (product) => Object.values(BRAND_META).filter((b) => product[b.flag]);

const matchesBrand = (product, brand) => {
  if (!brand || brand === 'all') return true;
  const meta = BRAND_META[brand];
  return meta ? Boolean(product[meta.flag]) : true;
};

const toMinutes = (form) => toEstimatedMinutes(form.amount, form.unit);

const validateForm = (form) => {
  const minutes = toMinutes(form);
  const empty = !String(form.amount).trim();
  let minErr = '';
  if (empty) {
    minErr = 'Durée requise';
  } else if (!Number.isInteger(minutes) || minutes < 5 || minutes > 960) {
    minErr = 'Entre 5 min et 16 h';
  }
  return {
    title: form.title.trim() ? '' : 'Saisissez un libellé',
    min: minErr,
  };
};

const BrandDot = ({ tone }) => <span className={`tp-mod__dot tp-mod__dot--${tone}`} aria-hidden />;

const WhoMenu = ({ users, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = users.find((u) => String(u.id) === String(value)) || null;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="tp-mod__who-menu" ref={ref}>
      <button
        type="button"
        className="tp-mod__who-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Personne prod par défaut"
        onClick={() => setOpen((v) => !v)}
      >
        {current ? (
          <span className="tp-mod__avatar" style={{ background: colorForUser(current.id) }}>
            {initials(current.name)}
          </span>
        ) : (
          <span className="tp-mod__avatar tp-mod__avatar--ghost" />
        )}
        <span className="tp-mod__who-label">{current?.name || 'Non assignée'}</span>
        <Icon icon="mdi:unfold-more-horizontal" width={16} aria-hidden />
      </button>
      {open ? (
        <div className="tp-mod__who-dropdown" role="listbox" aria-label="Choisir une personne">
          <button
            type="button"
            role="option"
            aria-selected={!current}
            className={`tp-mod__who-option${!current ? ' is-active' : ''}`}
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
          >
            <span className="tp-mod__avatar tp-mod__avatar--ghost" />
            <span className="tp-mod__who-option-name">Non assignée</span>
            {!current ? <Icon icon="mdi:check" width={16} className="tp-mod__who-check" /> : null}
          </button>
          {users.map((user) => {
            const active = String(value) === String(user.id);
            return (
              <button
                key={user.id}
                type="button"
                role="option"
                aria-selected={active}
                className={`tp-mod__who-option${active ? ' is-active' : ''}`}
                onClick={() => {
                  onChange(String(user.id));
                  setOpen(false);
                }}
              >
                <span className="tp-mod__avatar" style={{ background: colorForUser(user.id) }}>
                  {initials(user.name)}
                </span>
                <span className="tp-mod__who-option-name">{user.name}</span>
                {active ? <Icon icon="mdi:check" width={16} className="tp-mod__who-check" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const TempsModelesBoard = ({
  products = [],
  users = [],
  loading = false,
  onCreateTemplate,
  onDeleteTemplate,
}) => {
  const [query, setQuery] = useState('');
  const [sale, setSale] = useState('on');
  const [brand, setBrand] = useState('iti');
  const [openMap, setOpenMap] = useState({});
  const [forms, setForms] = useState({});
  const [pending, setPending] = useState({});
  const [flashId, setFlashId] = useState(null);

  const byBrandQuery = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((product) => {
      if (!matchesBrand(product, brand)) return false;
      if (!q) return true;
      return `${product.productRef || ''} ${product.productLabel || ''}`.toLowerCase().includes(q);
    });
  }, [products, brand, query]);

  const saleCounts = useMemo(() => {
    let on = 0;
    let off = 0;
    byBrandQuery.forEach((p) => {
      if (p.isTosell) on += 1;
      else off += 1;
    });
    return { on, off, all: byBrandQuery.length };
  }, [byBrandQuery]);

  const filtered = useMemo(() => {
    const list = byBrandQuery.filter((p) => {
      if (sale === 'on') return p.isTosell;
      if (sale === 'off') return !p.isTosell;
      return true;
    });
    return list.sort((a, b) => String(a.productLabel || a.productRef).localeCompare(String(b.productLabel || b.productRef), 'fr'));
  }, [byBrandQuery, sale]);

  const withTemplates = useMemo(
    () => filtered.filter((p) => (p.templates?.length || 0) > 0).length,
    [filtered],
  );

  const isFiltered = Boolean(query.trim()) || sale !== 'on' || brand !== 'iti';

  const resetFilters = () => {
    setQuery('');
    setSale('on');
    setBrand('iti');
  };

  const saleOptions = [
    { value: 'on', label: 'En vente', count: saleCounts.on },
    { value: 'off', label: 'Hors vente', count: saleCounts.off },
    { value: 'all', label: 'Tous', count: saleCounts.all },
  ];

  const brandOptions = [
    ...Object.values(BRAND_META).map((b) => ({
      value: b.key,
      label: (
        <span className="tp-mod__brand-opt">
          <BrandDot tone={b.tone} />
          {b.label}
        </span>
      ),
    })),
    { value: 'all', label: 'Toutes' },
  ];

  const getForm = (productId) => forms[productId] || emptyForm();

  const setForm = (productId, patch) => {
    setForms((prev) => ({
      ...prev,
      [productId]: { ...emptyForm(), ...prev[productId], ...patch },
    }));
  };

  const toggleOpen = (productId) => {
    setOpenMap((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const submitAdd = async (product) => {
    const form = getForm(product.id);
    const errors = validateForm(form);
    if (errors.title || errors.min) {
      setForm(product.id, { tried: true });
      return;
    }
    if (pending[`add-${product.id}`]) return;

    const estimatedMinutes = toMinutes(form);
    setPending((prev) => ({ ...prev, [`add-${product.id}`]: true }));
    try {
      const created = await onCreateTemplate?.({
        produitId: product.id,
        label: form.title.trim(),
        estimatedMinutes,
        defaultAssigneeId: form.who || null,
        active: true,
        sortOrder: (product.templates?.length || 0) + 1,
      });
      setForm(product.id, { ...emptyForm(), who: form.who, unit: form.unit });
      if (created?.id) {
        setFlashId(created.id);
        window.setTimeout(() => setFlashId((cur) => (cur === created.id ? null : cur)), 1400);
      }
    } catch {
      // toast déjà géré côté page
    } finally {
      setPending((prev) => {
        const next = { ...prev };
        delete next[`add-${product.id}`];
        return next;
      });
    }
  };

  const handleDelete = async (template) => {
    if (pending[`del-${template.id}`]) return;
    setPending((prev) => ({ ...prev, [`del-${template.id}`]: true }));
    try {
      await onDeleteTemplate?.(template);
    } catch {
      // toast déjà géré côté page
    } finally {
      setPending((prev) => {
        const next = { ...prev };
        delete next[`del-${template.id}`];
        return next;
      });
    }
  };

  const emptyTitle = query.trim()
    ? `Aucun produit pour « ${query.trim()} »`
    : 'Aucun produit pour ces filtres';

  const countLabel = `${filtered.length} produit${filtered.length > 1 ? 's' : ''} · ${withTemplates} avec tâches types`;

  return (
    <div className="tp-mod">
      <div className="tp-mod__toolbar">
        <label className="tp-mod__search">
          <Icon icon="mdi:magnify" width={16} aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Rechercher un produit"
            placeholder="Référence ou libellé"
          />
          {query ? (
            <button
              type="button"
              className="tp-mod__search-clear"
              aria-label="Effacer la recherche"
              onClick={() => setQuery('')}
            >
              <Icon icon="mdi:close" width={16} />
            </button>
          ) : null}
        </label>
        <AppPillSwitch
          variant="segment"
          options={saleOptions}
          value={sale}
          onChange={setSale}
          ariaLabel="État de vente"
        />
        <AppPillSwitch
          variant="segment"
          options={brandOptions}
          value={brand}
          onChange={setBrand}
          ariaLabel="Marque"
        />
        {isFiltered ? (
          <button type="button" className="tp-mod__reset" onClick={resetFilters}>
            <Icon icon="mdi:restore" width={14} aria-hidden />
            Réinitialiser
          </button>
        ) : null}
      </div>

      <div className="tp-mod__meta">
        <span className="tp-mod__count">{countLabel}</span>
      </div>

      {loading ? (
        <AppEmptyState loading />
      ) : filtered.length === 0 ? (
        <div className="tp-mod__empty">
          <div className="tp-mod__empty-icon">
            <Icon icon="mdi:magnify-close" width={22} />
          </div>
          <div className="tp-mod__empty-title">{emptyTitle}</div>
          <div className="tp-mod__empty-text">
            Essayez une autre référence, ou élargissez l’état de vente et la marque.
          </div>
          <AppButton variant="outline-secondary" onClick={resetFilters}>
            Réinitialiser les filtres
          </AppButton>
        </div>
      ) : (
        <>
          <div className="tp-mod__cols" aria-hidden>
            <span className="tp-mod__col-product">Produit</span>
            <span className="tp-mod__col-ref">Réf.</span>
            <span className="tp-mod__col-brands">Marques</span>
            <span className="tp-mod__col-tasks">Tâches types</span>
          </div>

          {filtered.map((product) => {
            const open = Boolean(openMap[product.id]);
            const templates = product.templates || [];
            const totalMin = templates.reduce((s, t) => s + (Number(t.estimatedMinutes) || 0), 0);
            const brands = productBrands(product);
            const form = getForm(product.id);
            const errors = validateForm(form);
            const titleErr = form.tried && Boolean(errors.title);
            const minErr = (form.tried || String(form.amount).trim() !== '') && Boolean(errors.min);
            const canSubmit = !errors.title && !errors.min;
            const addPending = Boolean(pending[`add-${product.id}`]);

            return (
              <div
                key={product.id}
                className={`tp-mod__row${open ? ' is-open' : ''}${product.isTosell ? '' : ' is-off'}`}
              >
                <button
                  type="button"
                  className="tp-mod__row-btn"
                  aria-expanded={open}
                  onClick={() => toggleOpen(product.id)}
                >
                  <Icon
                    icon="mdi:chevron-right"
                    width={16}
                    className={`tp-mod__chevron${open ? ' is-open' : ''}`}
                    aria-hidden
                  />
                  <span className="tp-mod__product">
                    <span className="tp-mod__label">{product.productLabel || product.productRef}</span>
                    {!product.isTosell ? (
                      <span className="tp-mod__off-badge">
                        <Icon icon="mdi:eye-off-outline" width={12} aria-hidden />
                        Hors vente
                      </span>
                    ) : null}
                  </span>
                  <span className="tp-mod__ref">{product.productRef}</span>
                  <span className="tp-mod__brands">
                    {brands.length === 0 ? (
                      <span className="tp-mod__muted">—</span>
                    ) : (
                      brands.map((b) => (
                        <span key={b.key} className="tp-mod__brand">
                          <BrandDot tone={b.tone} />
                          {b.label}
                        </span>
                      ))
                    )}
                  </span>
                  <span className="tp-mod__tasks-meta">
                    {templates.length > 0 ? (
                      <>
                        <span className="tp-mod__total">{formatMinutes(totalMin)}</span>
                        <span className="tp-mod__badge">{templates.length}</span>
                      </>
                    ) : (
                      <span className="tp-mod__none">Aucune</span>
                    )}
                  </span>
                </button>

                {open ? (
                  <div className="tp-mod__panel-wrap">
                    <div className="tp-mod__panel">
                      {!product.isTosell ? (
                        <div className="tp-mod__off-banner">
                          <Icon icon="mdi:eye-off-outline" width={13} aria-hidden />
                          Produit hors vente : les modèles restent appliqués aux commandes existantes qui le contiennent.
                        </div>
                      ) : null}

                      {templates.map((template, index) => {
                        const who = template.defaultAssignee;
                        const delPending = Boolean(pending[`del-${template.id}`]);
                        return (
                          <div
                            key={template.id}
                            className={`tp-mod__tpl${flashId === template.id ? ' is-flash' : ''}${delPending ? ' is-pending' : ''}`}
                          >
                            <span className="tp-mod__tpl-num">{index + 1}</span>
                            <span className="tp-mod__tpl-title">{template.label}</span>
                            <span className="tp-mod__tpl-min">{formatMinutes(template.estimatedMinutes)}</span>
                            <span className={`tp-mod__tpl-who${who ? '' : ' is-empty'}`}>
                              {who ? (
                                <span
                                  className="tp-mod__avatar"
                                  style={{ background: colorForUser(who.id) }}
                                >
                                  {initials(who.name)}
                                </span>
                              ) : (
                                <span className="tp-mod__avatar tp-mod__avatar--ghost" />
                              )}
                              <span className="tp-mod__tpl-who-name">
                                {who?.name || 'Non assignée'}
                              </span>
                            </span>
                            <button
                              type="button"
                              className="tp-mod__trash"
                              aria-label={`Supprimer « ${template.label} »`}
                              title="Supprimer ce modèle"
                              disabled={delPending}
                              onClick={() => handleDelete(template)}
                            >
                              {delPending ? (
                                <span className="tp-mod__spinner" aria-hidden />
                              ) : (
                                <Icon icon="mdi:trash-can-outline" width={16} />
                              )}
                            </button>
                          </div>
                        );
                      })}

                      {templates.length === 0 ? (
                        <div className="tp-mod__tpl-empty">
                          Aucune tâche type : les commandes contenant ce produit n’engendrent aucune tâche automatiquement.
                        </div>
                      ) : null}

                      <div className="tp-mod__form">
                        <div className="tp-mod__form-row">
                          <div className="tp-mod__field tp-mod__field--title">
                            <input
                              type="text"
                              value={form.title}
                              onChange={(e) => setForm(product.id, { title: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  submitAdd(product);
                                }
                              }}
                              aria-label="Libellé de la tâche type"
                              aria-invalid={titleErr}
                              placeholder="Nouvelle tâche type"
                              className={titleErr ? 'is-invalid' : ''}
                            />
                            {titleErr ? (
                              <span className="tp-mod__field-err">
                                <Icon icon="mdi:alert-circle-outline" width={12} aria-hidden />
                                {errors.title}
                              </span>
                            ) : null}
                          </div>

                          <div className="tp-mod__field tp-mod__field--min">
                            <TempsDurationInput
                              value={form.amount}
                              unit={form.unit}
                              invalid={minErr}
                              ariaLabel="Durée"
                              onChange={({ amount, unit }) => setForm(product.id, { amount, unit })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  submitAdd(product);
                                }
                              }}
                            />
                            {minErr ? (
                              <span className="tp-mod__field-err">
                                <Icon icon="mdi:alert-circle-outline" width={12} aria-hidden />
                                {errors.min}
                              </span>
                            ) : null}
                          </div>

                          <WhoMenu
                            users={users}
                            value={form.who}
                            onChange={(who) => setForm(product.id, { who })}
                          />

                          <button
                            type="button"
                            className={`tp-mod__add-btn${canSubmit ? ' is-ready' : ''}`}
                            disabled={addPending}
                            onClick={() => submitAdd(product)}
                          >
                            {addPending ? (
                              <span className="tp-mod__spinner tp-mod__spinner--light" aria-hidden />
                            ) : (
                              <Icon icon="mdi:plus" width={14} aria-hidden />
                            )}
                            Ajouter
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default TempsModelesBoard;
